import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery, useSubscription, useLazyQuery, useMutation } from '@apollo/client';
import { GET_SESSION, GET_SCENARIO, GET_EVALUATION_BY_SESSION, LIST_CONVERSATIONS } from '../../graphql/queries';
import { ON_SESSION_UPDATED } from '../../graphql/subscriptions';
import { UPDATE_SESSION } from '../../graphql/mutations';
import { formatTimestamp, calculateSessionDuration, generateMarkdownReport } from '../../utils/formatUtils';
import { uploadService } from '../../services/uploadService';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Download as DownloadIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { RootState } from '../../store';
import MarkdownDisplay from '../../components/common/MarkdownDisplay';
import rpEvaluationIcon from '../../assets/images/ic_evaluation.svg';
import aiCharacterIcon from '../../assets/images/ai_character.svg';
import rpConversationIcon from '../../assets/images/ic_conversation.svg';

/** 会話履歴の1回あたりの取得件数 */
const CONVERSATIONS_FETCH_LIMIT = 100;

const EvaluationPage: React.FC = () => {
  const { id: sessionId } = useParams<{ id: string }>();
  const { currentUser: user } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // セッションデータを取得
  const { 
    loading: sessionLoading, 
    error: sessionError, 
    data: sessionData 
  } = useQuery(GET_SESSION, {
    variables: { sessionId },
    skip: !sessionId,
    fetchPolicy: 'network-only'
  });

  // 評価データを取得するためのLazyクエリ
  const [
    getEvaluation,
    { loading: evaluationLoading, error: evaluationError, data: evaluationData }
  ] = useLazyQuery(GET_EVALUATION_BY_SESSION, {
    fetchPolicy: 'network-only'
  });

  // 会話履歴を取得するためのLazyクエリ
  const [
    getConversations,
    { loading: conversationsLoading, error: conversationsError, data: conversationsData }
  ] = useLazyQuery(LIST_CONVERSATIONS, {
    fetchPolicy: 'network-only'
  });

  // セッションからシナリオIDを取得し、シナリオデータを取得
  const { 
    loading: scenarioLoading, 
    error: scenarioError, 
    data: scenarioData 
  } = useQuery(GET_SCENARIO, {
    variables: { scenarioId: sessionData?.getSession?.scenarioId },
    skip: !sessionData?.getSession?.scenarioId
  });

  // セッションの更新をサブスクライブ
  const { data: subscriptionData } = useSubscription(ON_SESSION_UPDATED, {
    variables: { sessionId },
    skip: !sessionId
  });

  // 評価データの状態
  const [parsedEvaluationData, setParsedEvaluationData] = useState<any>(null);

  // セッションの状態を追跡する
  const [sessionStatus, setSessionStatus] = useState<string | null>(null);
  
  // 評価失敗状態を管理
  const [evaluationFailed, setEvaluationFailed] = useState<boolean>(false);

  // 評価スキップ状態（会話なし）を管理
  const [evaluationSkipped, setEvaluationSkipped] = useState<boolean>(false);
  // 会話履歴の追加ロード用
  const [conversationsNextToken, setConversationsNextToken] = useState<string | null>(null);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState<boolean>(false);
  const isLoadMoreConversationsRef = useRef<boolean>(false);

  // 評価を再実行するためのMutation
  const [retryEvaluation, { loading: retryLoading }] = useMutation(UPDATE_SESSION);

  // セッション変更時に会話の追加ロード用 state をリセット
  useEffect(() => {
    setConversationsNextToken(null);
    isLoadMoreConversationsRef.current = false;
    setLoadingMoreConversations(false);
  }, [sessionId]);

  // セッションデータが更新されたら状態を更新
  useEffect(() => {
    if (subscriptionData?.onSessionUpdated) {
      // サブスクリプションからの更新データを使用
      const updatedSession = subscriptionData.onSessionUpdated;
      
      // セッションのステータスを更新
      setSessionStatus(updatedSession.status);
      
      if (updatedSession.status === 'EVALUATED') {
        // console.log('セッションのステータスがEVALUATEDになりました。評価データと会話履歴を取得します。');
        // 評価データを取得
        getEvaluation({ variables: { sessionId } });
        // 会話履歴を取得
        getConversations({ variables: { sessionId, limit: CONVERSATIONS_FETCH_LIMIT } });
      } else if (updatedSession.status === 'EVALUATION_FAILED') {
        // 評価失敗状態を設定
        setEvaluationFailed(true);
      } else if (updatedSession.status === 'EVALUATION_SKIPPED') {
        // 評価スキップ状態を設定（会話なし）
        setEvaluationSkipped(true);
      }
    }
  }, [subscriptionData, sessionId, getEvaluation, getConversations]);

  // 初期データのロード
  useEffect(() => {
    if (!sessionLoading && sessionData?.getSession) {
      const session = sessionData.getSession;
      
      // セッションのステータスをチェック
      if (session.status !== 'COMPLETED' && session.status !== 'EVALUATED' && session.status !== 'EVALUATION_FAILED' && session.status !== 'EVALUATION_SKIPPED') {
        // エラーメッセージを表示してトップページに自動ナビゲート
        alert('このセッションは完了していません。再度、新しいセッションを開始してください。');
        navigate('/scenarios');
        return;
      }
      
      // 評価失敗状態の場合
      if (session.status === 'EVALUATION_FAILED') {
        setEvaluationFailed(true);
        return;
      }

      // 評価スキップ状態の場合（会話なし）
      if (session.status === 'EVALUATION_SKIPPED') {
        setEvaluationSkipped(true);
        return;
      }
      
      // 評価結果がある場合は取得
      if (session.status === 'EVALUATED') {
        // console.log('初期ロード時にセッションのステータスがEVALUATEDです。評価データと会話履歴を取得します。');
        // 評価データを取得
        getEvaluation({ variables: { sessionId } });
        // 会話履歴を取得
        getConversations({ variables: { sessionId, limit: CONVERSATIONS_FETCH_LIMIT } });
      }
    }
  }, [sessionData, sessionLoading, sessionId, getEvaluation, getConversations, navigate]);

  // 評価データと会話履歴が取得できたら処理（追加ロード時は追記、初回は置換）
  useEffect(() => {
    if (!conversationsData?.listConversations) return;

    const conversations = conversationsData.listConversations.items;
    const nextToken = conversationsData.listConversations.nextToken ?? null;
    const mapConversation = (conv: any) => ({
      id: `${conv.sessionId}-${conv.counter}`,
      userContent: conv.userContent,
      aiContent: conv.aiContent,
      aiParams: conv.aiParams,
      timestamp: formatTimestamp(conv.createdAt)
    });
    const mappedConversations = conversations.map(mapConversation);

    if (isLoadMoreConversationsRef.current) {
      // 追加ロード: 既存の会話に追記
      setLoadingMoreConversations(false);
      isLoadMoreConversationsRef.current = false;
      setConversationsNextToken(nextToken);
      setParsedEvaluationData((prev: any) => ({
        ...prev,
        conversations: [...(prev?.conversations || []), ...mappedConversations]
      }));
      return;
    }

    // 初回ロード: 評価データと会話を設定（evaluation/scenario が揃っている場合のみ）
    if (evaluationData?.getEvaluationBySession && scenarioData?.getScenario) {
      const evaluation = evaluationData.getEvaluationBySession;
      const scenario = scenarioData.getScenario;
      const session = sessionData?.getSession;
      setConversationsNextToken(nextToken);
      setParsedEvaluationData({
        evaluationText: evaluation.results || '',
        sessionId,
        scenarioId: session?.scenarioId,
        scenarioName: scenario.title,
        date: new Date(session?.endTime || session?.updatedAt || '').toLocaleDateString('ja-JP'),
        duration: calculateSessionDuration(session?.startTime || '', session?.endTime || ''),
        conversations: mappedConversations
      });
    }
  }, [evaluationData, conversationsData, scenarioData, sessionData, sessionId]);

  // ローディング状態の判定
  const loading = sessionLoading || 
                 (scenarioLoading && sessionData?.getSession?.scenarioId) || 
                 evaluationLoading || 
                 conversationsLoading || 
                 (!parsedEvaluationData && (sessionData?.getSession?.status === 'EVALUATED' || sessionStatus === 'EVALUATED'));

  // 会話履歴の追加ロード
  const loadMoreConversations = useCallback(() => {
    if (!sessionId || !conversationsNextToken || loadingMoreConversations) return;
    isLoadMoreConversationsRef.current = true;
    setLoadingMoreConversations(true);
    getConversations({ variables: { sessionId, limit: CONVERSATIONS_FETCH_LIMIT, nextToken: conversationsNextToken } });
  }, [sessionId, conversationsNextToken, loadingMoreConversations, getConversations]);

  // 最下部到達時に追加ロード（Intersection Observer）
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const conversationScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    const scrollRoot = conversationScrollRef.current;
    if (!sentinel || !scrollRoot || !conversationsNextToken || loadingMoreConversations) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreConversations();
        }
      },
      { root: scrollRoot, rootMargin: '100px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [conversationsNextToken, loadingMoreConversations, loadMoreConversations]);

  // エラー表示
  if (sessionError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          セッションデータの取得中にエラーが発生しました
        </Typography>
        <Typography variant="body2">{sessionError.message}</Typography>
      </Box>
    );
  }

  if (scenarioError && sessionData?.getSession?.scenarioId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          シナリオデータの取得中にエラーが発生しました
        </Typography>
        <Typography variant="body2">{scenarioError.message}</Typography>
      </Box>
    );
  }

  // 評価を再実行する関数
  const handleRetryEvaluation = async () => {
    if (!sessionId) return;
    
    try {
      // 評価失敗状態をリセット
      setEvaluationFailed(false);
      
      // セッションのステータスをCOMPLETEDに戻して評価を再トリガー
      await retryEvaluation({
        variables: { 
          input: {
            sessionId,
            status: 'COMPLETED'
          }
        }
      });
      
      console.log('評価の再実行を開始しました');
    } catch (error) {
      console.error('評価の再実行に失敗しました:', error);
      // エラーが発生した場合は再度失敗状態に戻す
      setEvaluationFailed(true);
      alert('評価の再実行に失敗しました。しばらく時間をおいてから再度お試しください。');
    }
  };

  const handleBackToScenarios = () => {
    // シナリオ一覧に戻る
    navigate('/scenarios');
  };

  // 評価スキップ時（会話なし）の表示 - 再作成ボタンは出さない
  if (evaluationSkipped) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh',
        flexDirection: 'column',
        gap: 3,
        p: 3
      }}>
        <Typography variant="h6" sx={{ 
          textAlign: 'center',
          fontSize: '18px',
          fontWeight: 600,
          lineHeight: 1.5,
          letterSpacing: '0.1em'
        }}>
          会話が記録されていませんでした
        </Typography>
        
        <Typography variant="body2" sx={{ 
          textAlign: 'center',
          color: '#666666',
          fontSize: '16px',
          lineHeight: 1.5,
          letterSpacing: '0.1em',
          mb: 2
        }}>
          このセッションでは会話が行われていません。<br />
          ロールプレイを実施した後、再度セッションを終了すると評価をご確認いただけます。
        </Typography>

        <Button
          onClick={handleBackToScenarios}
          variant="contained"
          sx={{
            backgroundColor: theme.palette.smartSkillTalk.linkBlue,
            color: '#FFFFFF',
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: 1.5,
            letterSpacing: '0.1em',
            padding: '12px 24px',
            borderRadius: '10px',
            minWidth: '200px',
            '&:hover': {
              backgroundColor: '#3687DF',
            }
          }}
        >
          シナリオ一覧に戻る
        </Button>
      </Box>
    );
  }

  // 評価失敗時のエラー表示
  if (evaluationFailed) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh',
        flexDirection: 'column',
        gap: 3,
        p: 3
      }}>
        <Typography variant="h6" color="error" sx={{ 
          textAlign: 'center',
          fontSize: '18px',
          fontWeight: 600,
          lineHeight: 1.5,
          letterSpacing: '0.1em'
        }}>
          AIフィードバックの作成に失敗しました
        </Typography>
        
        <Typography variant="body2" sx={{ 
          textAlign: 'center',
          color: '#666666',
          fontSize: '16px',
          lineHeight: 1.5,
          letterSpacing: '0.1em',
          mb: 2
        }}>
          システムの問題により、AIからのフィードバックを作成できませんでした。<br />
          下記のボタンから再度作成をお試しいただくか、シナリオ一覧に戻ってください。
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Button
            onClick={handleRetryEvaluation}
            disabled={retryLoading}
            variant="contained"
            sx={{
              backgroundColor: theme.palette.smartSkillTalk.green,
              color: '#FFFFFF',
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: 1.5,
              letterSpacing: '0.1em',
              padding: '12px 24px',
              borderRadius: '10px',
              minWidth: '200px',
              '&:hover': {
                backgroundColor: '#86CA04',
              },
              '&:disabled': {
                backgroundColor: '#cccccc',
              }
            }}
          >
            {retryLoading ? 'フィードバックを作成中...' : 'フィードバックを再作成する'}
          </Button>
          <Button
            onClick={handleBackToScenarios}
            variant="contained"
            sx={{
              backgroundColor: theme.palette.smartSkillTalk.linkBlue,
              color: '#FFFFFF',
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: 1.5,
              letterSpacing: '0.1em',
              padding: '12px 24px',
              borderRadius: '10px',
              minWidth: '200px',
              '&:hover': {
                backgroundColor: '#3687DF',
              }
            }}
          >
            シナリオ一覧に戻る
          </Button>
        </Box>
      </Box>
    );
  }

  // ローディング表示
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          {!sessionData ? '評価データを読み込んでいます...' : 
           sessionData.getSession.status !== 'EVALUATED' ? '評価結果を生成中です...' : 
           '評価結果を読み込んでいます...'}
        </Typography>
      </Box>
    );
  }

  // 評価結果がまだ生成されていない場合
  if (sessionData?.getSession?.status !== 'EVALUATED' && sessionStatus !== 'EVALUATED') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', flexDirection: 'column' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          評価結果を生成中です...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          しばらくお待ちください
        </Typography>
      </Box>
    );
  }

  // シナリオデータ
  const scenario = scenarioData?.getScenario;

  // レポートをダウンロードする関数
  const handleDownloadReport = async () => {
    if (!scenario || !parsedEvaluationData || !sessionId) {
      alert('レポートを生成するためのデータが不足しています。');
      return;
    }

    // 最新の評価データを取得
    const { data: latestEvaluationData } = await getEvaluation({ 
      variables: { sessionId },
      fetchPolicy: 'network-only' // キャッシュを使わず、必ず最新データを取得
    });

    if (!latestEvaluationData?.getEvaluationBySession) {
      alert('評価データの取得に失敗しました。');
      return;
    }

    // PDFがあればそれを表示、PDFがなければエラーを表示
    if(latestEvaluationData.getEvaluationBySession.resultPdfPath) {
      try {
        const { presignedUrl } = await uploadService.getEvaluationUrl(latestEvaluationData.getEvaluationBySession.evaluationId);
        window.open(presignedUrl, '_blank');
      } catch (error: any) {
        console.error('PDFを開く際にエラーが発生しました:', error);
      }
    } else {
      alert('現在、フィードバックのPDFを生成しています。生成完了まで少々お待ちください。');
      return;
      /*
      // 評価情報オブジェクトを作成
      const evaluationInfo = {
        session: sessionData?.getSession,
        evaluationText: parsedEvaluationData.evaluationText,
        conversations: parsedEvaluationData.conversations,
        date: parsedEvaluationData.date,
        duration: parsedEvaluationData.duration
      };

      // Markdownレポートを生成
      const markdown = generateMarkdownReport(scenario, evaluationInfo, user?.displayName );
      
      if (!markdown) {
        alert('レポートを生成できませんでした。');
        return;
      }

      // Blobオブジェクトを作成
      const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
      
      // ダウンロードリンクを作成
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // ファイル名を設定
      const fileName = `評価レポート_${scenario.title || 'シナリオ'}_${new Date().toISOString().split('T')[0]}.txt`;
      link.download = fileName;
      
      // リンクをクリックしてダウンロード
      document.body.appendChild(link);
      link.click();
      
      // クリーンアップ
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      */
    }
    // console.log('評価レポートをダウンロードしました。');
  };

  return (
    <Box>
      {/* H2エリア */}
      <Box className="h2-area" sx={{ width: '100%', pb: 4 }}>
        <Box className="h2-container" sx={{ display: 'flex', alignItems: 'center' }}>
          <img 
            src={rpEvaluationIcon} 
            alt="AI評価アイコン" 
            style={{ width: '24px', height: '24px', marginRight: '10px' }}
          />
          <Typography variant="h4" sx={{ 
            fontSize: '24px', 
            fontWeight: 700, 
            lineHeight: 1.5, 
            letterSpacing: '0.1em',
            color: theme.palette.smartSkillTalk.linkBlue
          }}>
            AIからのフィードバック
          </Typography>
        </Box>
      </Box>

      <Box className="feedback-area" sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        gap: '30px',
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'stretch'
      }}>
        {/* 左側: 評価サマリー */}
        <Box className="feedback-left" sx={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: '40px',
          flex: { xs: '1', md: '8' },
          width: '100%'
        }}>
          {/* プロパティエリア */}
          <Box className="prop-area" sx={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: '15px',
            width: '100%'
          }}>
            <Box className="prop-item" sx={{ 
              display: 'flex',
              flexWrap: 'wrap',
              gap: '15px',
              paddingBottom: '10px'
            }}>
              <Box sx={{
                display: 'flex',
                flexDirection: isSmallScreen ? 'column' : 'row',
                alignItems: 'flex-start',
                gap: '15px',
                width: '100%'
              }}>
                <Box className="prop-title" sx={{ 
                  backgroundColor: theme.palette.smartSkillTalk.aiBlue,
                  color: '#FFFFFF',
                  padding: '5px 17px',
                  borderRadius: '4px',
                  fontSize: '16px',
                  whiteSpace: 'nowrap',
                  width: isSmallScreen ? '100%' : '99px',
                  minWidth: isSmallScreen ? 'auto' : '99px',
                  textAlign: 'center',
                  flexShrink: 0
                }}>
                  タイトル
                </Box>
                <Typography sx={{
                  fontSize: '16px',
                  lineHeight: '1.5em',
                  letterSpacing: '0.1em',
                  color: '#666666',
                  whiteSpace: 'pre-line',
                  flex: 1
                }}>
                  {scenario?.title || parsedEvaluationData.scenarioName}
                </Typography>
              </Box>
            </Box>

            <Box className="prop-item" sx={{ 
              display: 'flex',
              flexWrap: 'wrap',
              gap: '15px',
              paddingBottom: '10px'
            }}>
              <Box sx={{
                display: 'flex',
                flexDirection: isSmallScreen ? 'column' : 'row',
                alignItems: 'flex-start',
                gap: '15px',
                width: '100%'
              }}>
                <Box className="prop-title" sx={{ 
                  backgroundColor: theme.palette.smartSkillTalk.aiBlue,
                  color: '#FFFFFF',
                  padding: '5px 17px',
                  borderRadius: '4px',
                  fontSize: '16px',
                  whiteSpace: 'nowrap',
                  width: isSmallScreen ? '100%' : '99px',
                  minWidth: isSmallScreen ? 'auto' : '99px',
                  textAlign: 'center',
                  flexShrink: 0
                }}>
                  目的
                </Box>
                <Typography sx={{
                  fontSize: '16px',
                  lineHeight: '1.5em',
                  letterSpacing: '0.1em',
                  color: '#666666',
                  whiteSpace: 'pre-line',
                  flex: 1
                }}>
                  {scenario?.goal || '不明'}
                </Typography>
              </Box>
            </Box>
            
            <Box className="prop-item" sx={{ 
              display: 'flex',
              flexWrap: 'wrap',
              gap: '15px',
              paddingBottom: '10px'
            }}>
              <Box sx={{
                display: 'flex',
                flexDirection: isSmallScreen ? 'column' : 'row',
                alignItems: 'flex-start',
                gap: '15px',
                width: '100%'
              }}>
                <Box className="prop-title" sx={{ 
                  backgroundColor: theme.palette.smartSkillTalk.aiBlue,
                  color: '#FFFFFF',
                  padding: '5px 17px',
                  borderRadius: '4px',
                  fontSize: '16px',
                  whiteSpace: 'nowrap',
                  width: isSmallScreen ? '100%' : '99px',
                  minWidth: isSmallScreen ? 'auto' : '99px',
                  textAlign: 'center',
                  flexShrink: 0
                }}>
                  時間
                </Box>
                <Typography sx={{
                  fontSize: '16px',
                  lineHeight: '1.5em',
                  letterSpacing: '0.1em',
                  color: '#666666',
                  whiteSpace: 'pre-line',
                  flex: 1
                }}>
                  {parsedEvaluationData.duration} （目安時間：{scenario?.estimatedTime || '30'}分）
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {/* ボタンエリア */}
          <Box className="buttons-area" sx={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '18px',
            padding: '0 5px',
            width: '100%',
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Button 
              className="btn-download"
              onClick={handleDownloadReport}
              sx={{ 
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '4px',
                padding: '12px',
                backgroundColor: theme.palette.smartSkillTalk.green,
                borderRadius: '10px',
                color: '#FFFFFF',
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: 1.5,
                letterSpacing: '0.1em',
                cursor: 'pointer',
                border: 'none',
                height: '52px',
                width: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  backgroundColor: '#86CA04',
                }
              }}
              startIcon={<DownloadIcon />}
            >
              フィードバックをダウンロード
            </Button>
            <Button 
              className="btn-return"
              onClick={handleBackToScenarios}
              sx={{ 
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '16px',
                padding: '14px 24px',
                backgroundColor: theme.palette.smartSkillTalk.linkBlue,
                borderRadius: '10px',
                color: '#FFFFFF',
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: 1.5,
                letterSpacing: '0.1em',
                cursor: 'pointer',
                border: 'none',
                height: '52px',
                width: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  backgroundColor: '#3687DF',
                }
              }}
              startIcon={<CloseIcon />}
            >
              終了して戻る
            </Button>
          </Box>
          
          {/* AIフィードバックエリア */}
          <Box className="ai-feedback-area" sx={{ 
            backgroundColor: '#F0F7FF',
            border: `2px solid ${theme.palette.smartSkillTalk.aiBlue}`,
            borderRadius: '10px',
            padding: '20px',
            width: '100%'
          }}>
            <Box sx={{ display: 'flex' }}>
              {/* AIアイコンとヘッダー */}
              <Box className="ai-character-icon">
                <img 
                  src={aiCharacterIcon} 
                  alt="AIアイコン" 
                />
              </Box>
              <Box className="ai-feedback-header" sx={{ marginBottom: '20px', marginLeft: '10px' }}>
                <Typography sx={{ 
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: 1.5,
                  letterSpacing: '0.1em',
                  color: theme.palette.smartSkillTalk.linkBlue
                }}>
                  AIがロープレ内容について評価します
                </Typography>
                <Typography variant="h5" component="h3" sx={{ 
                  fontSize: '20px',
                  fontWeight: 700,
                  lineHeight: 1.5,
                  letterSpacing: '0.1em',
                  color: theme.palette.smartSkillTalk.linkBlue,
                  marginTop: '5px'
                }}>
                  AIからのフィードバック
                </Typography>
              </Box>
            </Box>
            
            {/* フィードバックセクション */}
            <Box>
              <MarkdownDisplay markdownText={parsedEvaluationData.evaluationText} />
            </Box>
          </Box>
        </Box>
        
        {/* 右側: 会話履歴 */}
        <Box className="feedback-right" sx={{ 
          display: 'flex',
          flexDirection: 'column',
          flex: { xs: '1', md: '4' },
          width: '100%',
          minHeight: 0  /* flex 子要素で overflow を有効にするため */
        }}>
          {/* 会話記録ヘッダー */}
          <Box className="conversation-record" sx={{ 
            backgroundColor: theme.palette.smartSkillTalk.linkBlue,
            color: '#FFFFFF',
            borderRadius: '10px 10px 0 0',
            padding: '10px 15px',
            display: 'flex',
            justifyContent: 'center',
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <Box>
                <img 
                  src={rpConversationIcon} 
                  alt="会話アイコン" 
                />
              </Box>
              <Typography variant="h6" component="h3" sx={{ 
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: 1.5,
                letterSpacing: '0.1em',
                color: '#FFFFFF'
              }}>
                ロープレ会話記録
              </Typography>
            </Box>
          </Box>
          
          {/* 会話背景 */}
          <Box
            ref={conversationScrollRef}
            className="conversation-bg"
            sx={{ 
              backgroundColor: '#F0F0F0',
              border: `2px solid ${theme.palette.smartSkillTalk.linkBlue}`,
              flex: 1,
              minHeight: 0,  /* flex 子要素で overflow を有効にするため */
              maxHeight: '200vh',  /* 高さを制限してスクロールバーを表示 */
              overflow: 'auto',
              padding: '20px'
            }}
          >
            {/* 会話バブル */}
            {parsedEvaluationData.conversations && parsedEvaluationData.conversations.map((conversation: any, index: number) => (
              <React.Fragment key={conversation.id}>
                {/* 担当者（ユーザー）のメッセージ */}
                {conversation.userContent && (
                  <Box 
                    className={`conversation-bubble bubble-a bubble-${index + 1}`}
                    sx={{ 
                      display: 'flex',
                      justifyContent: 'flex-end',
                      marginBottom: '20px',
                      width: '100%'
                    }}
                  >
                    <Box sx={{
                      padding: '10px',
                      borderRadius: '10px',
                      maxWidth: '80%',
                      fontSize: '14px',
                      fontWeight: 400,
                      lineHeight: 1.428,
                      letterSpacing: '0.1em',
                      backgroundColor: theme.palette.smartSkillTalk.aiBlue,
                      color: '#FFFFFF',
                      alignSelf: 'flex-end'
                    }}>
                      {conversation.userContent}
                    </Box>
                  </Box>
                )}
                
                {/* お客様（AI）のメッセージ */}
                {conversation.aiContent && (
                  <Box 
                    className={`conversation-bubble bubble-b bubble-${index + 2}`}
                    sx={{ 
                      display: 'flex',
                      justifyContent: 'flex-start',
                      marginBottom: '20px',
                      width: '100%'
                    }}
                  >
                    <Box sx={{
                      padding: '10px',
                      borderRadius: '10px',
                      maxWidth: '80%',
                      fontSize: '14px',
                      fontWeight: 400,
                      lineHeight: 1.428,
                      letterSpacing: '0.1em',
                      backgroundColor: '#FFFFFF',
                      color: '#333333',
                      alignSelf: 'flex-start'
                    }}>
                      {conversation.aiContent}
                    </Box>
                  </Box>
                )}
              </React.Fragment>
            ))}
            {/* スクロール最下部到達時に追加ロードをトリガーするセンチネル */}
            {conversationsNextToken && (
              <Box
                ref={loadMoreSentinelRef}
                sx={{ height: 1, minHeight: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}
              >
                {loadingMoreConversations && <CircularProgress size={24} />}
              </Box>
            )}
          </Box>
          
        </Box>
      </Box>
    </Box>
  );
};

export default EvaluationPage;
